/**
 * APEX v7.7.1 — Objectives ↔ Games
 *
 * Lien entre une game et les objectifs actifs (mission du jour, blocs).
 * Pour chaque game, on retourne la liste des "contributions" (cette game
 * fait-elle avancer un objectif ? ou le casse-t-elle ?).
 *
 * Utilisé par GameHistoryCard pour afficher inline "✓ contribue à la mission".
 *
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md section 6.
 */

import { evaluateGameContribution, isMissionForToday } from './dailyMission';

/**
 * Pour une game donnée, retourne les contributions aux objectifs actifs.
 *
 * @param {Object} game - la game à évaluer
 * @param {Array} miniBlocks - tous les mini_blocks de l'user
 * @returns {Array} [{ status, objective, label, reason, progress?, target? }]
 */
export function getGameContributions(game, miniBlocks) {
  if (!game || !Array.isArray(miniBlocks)) return [];

  // Game annulée → pas de contribution
  if (isCancelled(game)) return [];

  const contributions = [];

  for (const block of miniBlocks) {
    // Mission du jour : on évalue uniquement si la mission est pour aujourd'hui
    // ET que la game est arrivée le jour de la mission
    if (block.type === 'daily_mission') {
      // Une game contribue à la mission du jour SI :
      //   - la mission est pour aujourd'hui (ou pour le jour de la game)
      //   - statut active ou in_progress (pas skipped/expired/completed avant cette game)
      const gameDate = getGameDateKey(game);
      const missionDate = block.data?.created_for_date;
      if (gameDate !== missionDate) continue;
      if (!['active', 'in_progress', 'completed'].includes(block.status)) continue;

      const { contributes, reason } = evaluateGameContribution(game, block);
      const progress = block.data?.progress?.gamesContributed || 0;
      const target = block.target_games || block.data?.target?.games || 1;

      contributions.push({
        status: contributes ? 'contributes' : 'broke',
        objective: block,
        label: block.data?.label || 'Mission du jour',
        reason,
        progress,
        target,
        objectiveType: 'daily_mission'
      });
      continue;
    }

    // Blocs custom : évalués si actifs ET game pendant la période d'activité
    if (block.type === 'custom' || !block.type) {
      // Vérifier que le bloc est actif et que la game est postérieure à la création
      if (!['active', 'in_progress'].includes(block.status)) continue;
      const gameTs = new Date(game.timestamp).getTime();
      const blockTs = new Date(block.created_at).getTime();
      if (gameTs < blockTs) continue;

      const contribution = evaluateCustomBlockContribution(game, block);
      if (contribution.status !== 'irrelevant') {
        contributions.push(contribution);
      }
      continue;
    }
  }

  return contributions;
}

/**
 * Évalue si une game contribue à un bloc custom.
 * Format des blocs custom historiques : block.data.criterion (champion, max_deaths, etc.)
 */
function evaluateCustomBlockContribution(game, block) {
  const data = block.data || {};
  const criterion = data.criterion || data.type;
  const target = block.target_games || data.target_games || 10;
  const progress = block.total_games || 0;
  const label = data.label || data.title || 'Bloc actif';

  switch (criterion) {
    case 'greed_deaths_max':
    case 'max_greed_deaths': {
      const greedCount = (game.deathDetails || []).filter(d => d?.type === 'greed').length;
      const maxAllowed = data.max_greed_per_game ?? data.maxGreed ?? 1;
      return {
        status: greedCount <= maxAllowed ? 'contributes' : 'broke',
        objective: block,
        label,
        reason: greedCount <= maxAllowed
          ? `${greedCount} greed (≤${maxAllowed}) ✓`
          : `${greedCount} greed (>${maxAllowed}) ✗`,
        progress,
        target,
        objectiveType: 'custom_block'
      };
    }
    case 'champion_focus': {
      const champion = data.champion;
      const match = game.champion === champion;
      return match
        ? {
            status: 'contributes',
            objective: block,
            label,
            reason: `Joué sur ${champion} ✓`,
            progress,
            target,
            objectiveType: 'custom_block'
          }
        : { status: 'irrelevant' };
    }
    case 'cs_per_min': {
      const minCs = data.min_cs_per_min ?? 8;
      const csPerMin = game.csPerMin || 0;
      return {
        status: csPerMin >= minCs ? 'contributes' : 'broke',
        objective: block,
        label,
        reason: csPerMin >= minCs
          ? `${csPerMin.toFixed(1)} CS/min (≥${minCs}) ✓`
          : `${csPerMin.toFixed(1)} CS/min (<${minCs}) ✗`,
        progress,
        target,
        objectiveType: 'custom_block'
      };
    }
    default:
      return { status: 'irrelevant' };
  }
}

/**
 * Helper : détecte une game annulée.
 */
function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

/**
 * Retourne le "jour" d'une game au format YYYY-MM-DD (timezone locale).
 */
function getGameDateKey(game) {
  const ts = game.timestamp;
  if (!ts) return null;
  const d = new Date(ts);
  // YYYY-MM-DD en local
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
